import React, { useState } from 'react';
import * as mentorApi from '../../api/mentorApi';

const GENERIC_ERROR = "Couldn't change the chair - please try again.";

const councilsLabel = (councils) => {
  if (!councils || councils.length === 0) return '—';
  return councils.length === 1 ? `Council ${councils[0]}` : `Councils ${councils[0]}–${councils[councils.length - 1]}`;
};

// Mission chairs for one squad. A mission's chair can be set or changed by staff (program administrator, the
// squad's SPOC, an assigned auditor) until one of its councils has started or has activity; after that the row
// is locked. The server enforces the rule - `editable` only decides whether the button is offered.
export default function EsgChairAllocation({ squadId, missions, members, onChanged, onSuccess, onError }) {
  const [editing, setEditing] = useState(null); // mission number
  const [studentId, setStudentId] = useState('');
  const [confirmName, setConfirmName] = useState(null); // set when the server wants a duplicate confirmed
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const start = (mission) => {
    setEditing(mission.mission_number);
    setStudentId(mission.student_id ? String(mission.student_id) : '');
    setConfirmName(null);
    setError('');
  };

  const cancel = () => {
    setEditing(null);
    setConfirmName(null);
    setError('');
  };

  const save = async (confirmDuplicate = false) => {
    if (!studentId) return;
    setBusy(true);
    setError('');
    try {
      await mentorApi.updateEsgChair(squadId, editing, Number(studentId), confirmDuplicate);
      onSuccess?.(`Mission ${editing} chair updated.`);
      cancel();
      await onChanged?.();
    } catch (err) {
      if (err?.status === 409) {
        setConfirmName(members.find((m) => String(m.student_id) === studentId)?.student?.name || 'This member');
      } else {
        setError(GENERIC_ERROR);
        onError?.(GENERIC_ERROR);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="students-table-card esg-table-card-spaced">
      <table className="students-table">
        <thead>
          <tr><th>Mission</th><th>Councils</th><th>Chair</th><th className="esg-col-shrink"></th></tr>
        </thead>
        <tbody>
          {missions.map((m) => (
            <tr key={m.mission_number}>
              <td><strong>{`Mission ${m.mission_number}`}</strong></td>
              <td>{councilsLabel(m.councils)}</td>
              <td>
                {editing === m.mission_number ? (
                  <div>
                    <select
                      className="form-control-select esg-select-auto"
                      aria-label={`Chair for Mission ${m.mission_number}`}
                      value={studentId}
                      onChange={(e) => { setError(''); setConfirmName(null); setStudentId(e.target.value); }}
                    >
                      <option value="">Select a member</option>
                      {members.map((mem) => (
                        <option key={mem.student_id} value={mem.student_id}>{mem.student?.name || 'Student'}</option>
                      ))}
                    </select>
                    {confirmName && (
                      <div className="rejection-banner esg-mt-8" role="alert">
                        {`${confirmName} is already chair for another mission of this squad. Assign anyway?`}
                        <div className="esg-confirm-row">
                          <button type="button" className="esg-btn esg-btn-primary" disabled={busy} onClick={() => save(true)}>Assign anyway</button>
                          <button type="button" className="esg-btn esg-btn-secondary" onClick={cancel}>Cancel</button>
                        </div>
                      </div>
                    )}
                    {error && <div className="rejection-banner esg-mt-8" role="alert">{error}</div>}
                  </div>
                ) : (
                  <>
                    {m.name || <span className="esg-muted">Not set</span>}
                    {(m.council_chairs || []).some((c) => c.stand_in) && (
                      <ul className="esg-council-chairs">
                        {m.council_chairs.map((c) => (
                          <li key={c.council_number}>
                            {`Council ${c.council_number}: ${c.name || 'Not set'}`}
                            {c.stand_in && <strong>{' (stand-in)'}</strong>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </td>
              <td className="esg-nowrap">
                {editing === m.mission_number ? (
                  !confirmName && (
                    <span className="esg-inline-actions">
                      <button type="button" className="esg-btn esg-btn-primary" disabled={busy || !studentId} onClick={() => save(false)}>Save</button>
                      <button type="button" className="esg-btn esg-btn-secondary" onClick={cancel}>Cancel</button>
                    </span>
                  )
                ) : m.editable ? (
                  <button type="button" className="esg-btn esg-btn-secondary" aria-label={`Edit chair for Mission ${m.mission_number}`} disabled={editing !== null} onClick={() => start(m)}>
                    Edit
                  </button>
                ) : (
                  <span className="checklist-row-badge" title="A council of this mission has started, so its chair is locked">Started</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
